import React, { useState, useRef } from 'react';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { FieldItem, Values } from '@lib/interfaces/form';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import DateRangePicker from '@lib/components/DateTimePicker/DateRangePicker';
import { InputSizes, InputTypes } from '@lib/components/Input/enums';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import TasksReportingFilterActions from './TasksReportingFilterActions';
import styles from './TasksReportingFilter.module.scss';

type Props = {
  counter: number;
  defaultValues: Values;
  filterFields: FieldItem[];
  maxDate?: Dayjs;
  minDate?: Dayjs;
  onPeriodChange: (v: string[]) => void;
  onResetFilters: () => void;
  onSubmitFilter: (values: Values) => void;
  periodValue: string[];
};

export default function TasksReportingFilter(props: Props) {
  const {
    counter,
    defaultValues,
    filterFields,
    maxDate,
    minDate,
    onPeriodChange,
    onResetFilters,
    onSubmitFilter,
    periodValue,
  } = props;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<FormRef>(undefined);

  return (
    <>
      <WhiteBox
        className={styles.filterCard}
        verticalPaddings="small"
        horizontalPaddings="small"
      >
        <div className={styles.filterActions}>
          <Chip
            counter={counter}
            label={t('filter')}
            leftIcon="filter_alt"
            leftIconSymbolsOutlined
            onClick={() => setIsOpen(true)}
            size={ChipSizes.large}
            type={ChipTypes.chipFilter}
            tooltipForCroppedTextDisabled
          />
          {counter > 0 && (
            <Button
              buttonSize={ButtonSizes.large}
              buttonText={t('reset')}
              buttonType={ButtonTypes.primaryText}
              onClick={onResetFilters}
            />
          )}
        </div>
        <div className={styles.dateRangePickerCol}>
          <DateRangePicker
            label={t('period')}
            onChange={onPeriodChange}
            size={InputSizes.medium}
            type={InputTypes.dateRange}
            value={periodValue}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      </WhiteBox>
      <Modal
        isOpen={isOpen}
        caption={t('filter')}
        onClose={() => setIsOpen(false)}
      >
        <Form
          formRef={formRef}
          fields={filterFields}
          onSubmit={(values) => {
            onSubmitFilter(values);
            setIsOpen(false);
          }}
          defaultValues={defaultValues}
          actionsComponent={TasksReportingFilterActions}
          actionsComponentProps={{
            onCancel: () => setIsOpen(false),
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}
